import type { WindowDefinition, WindowRegistration, WindowViewState } from './types';
import { loadWindowExtras, loadWindowState, saveWindowState } from './WindowPersistence';
import {
    DEFAULT_OPEN_WINDOW_PREFIXES,
    WINDOW_STATE_CLOSED,
    WINDOW_STATE_OPEN,
} from './constants';

export interface WindowHandle {
    id: string;
    setVisible(visible: boolean): void;
    bringToFront(): void;
    getState(): WindowRegistration['state'];
    setState(state: WindowRegistration['state']): void;
    getExtras(): WindowRegistration['extras'];
    setExtras(extras: WindowRegistration['extras']): void;
    getWindowState(): WindowViewState;
    setWindowState(state: WindowViewState): void;
    getActiveTab(): string;
    setActiveTab(tab: string): void;
    destroy(): void;
}

interface WindowRuntime {
    isDragging: boolean;
    isResizing: boolean;
    dragStartX: number;
    dragStartY: number;
    startX: number;
    startY: number;
    startWidth: number;
    startHeight: number;
    zIndex: number;
    onHeaderMouseDown: (e: MouseEvent) => void;
    onResizeMouseDown?: (e: MouseEvent) => void;
    onContainerMouseDown: () => void;
}

interface WindowRecord extends WindowRegistration {
    runtime: WindowRuntime;
    stateChangeTimeoutId: number | null;
}

export class WindowRegistry {
    private readonly windows = new Map<string, WindowRecord>();
    private topZIndex = 2000;
    private activePointerWindowId: string | null = null;
    private readonly onMouseMove = (e: MouseEvent): void => this.handleGlobalMouseMove(e);
    private readonly onMouseUp = (): void => this.handleGlobalMouseUp();
    private readonly onWindowResize = (): void => this.handleWindowResize();
    private listenersAttached = false;

    public register(definition: WindowDefinition): WindowHandle {
        const existing = this.windows.get(definition.id);
        if (existing) {
            throw new Error(`Window with id "${definition.id}" is already registered.`);
        }

        const persistState = definition.persistState !== false;
        const state = persistState
            ? loadWindowState(
                definition.id,
                {
                    position: definition.defaultPosition,
                    size: definition.defaultSize,
                },
                definition.minWidth,
                definition.minHeight
            )
            : {
                position: { ...definition.defaultPosition },
                size: { ...definition.defaultSize },
            };
        const extras = this.normalizeExtras(
            definition,
            persistState ? loadWindowExtras(definition.id, definition.defaultExtras ?? {}) : (definition.defaultExtras ?? {})
        );

        const runtime = this.createRuntime(definition);
        const record: WindowRecord = { definition, state, extras, runtime, stateChangeTimeoutId: null };
        this.windows.set(definition.id, record);
        this.attachGlobalListeners();
        this.applyWindowLayout(definition, state);
        this.applyViewState(record, { suppressCallbacks: true });
        if (record.extras.windowState !== WINDOW_STATE_CLOSED) {
            this.bringToFront(definition.id);
        }

        return {
            id: definition.id,
            setVisible: (visible: boolean) => {
                record.extras = this.normalizeExtras(definition, {
                    ...record.extras,
                    windowState: visible ? 'open' : 'closed',
                });
                this.applyViewState(record);
                this.save(definition.id);
            },
            bringToFront: () => this.bringToFront(definition.id),
            getState: () => {
                const registration = this.windows.get(definition.id);
                if (!registration) {
                    return state;
                }
                return registration.state;
            },
            setState: (nextState) => {
                const registration = this.windows.get(definition.id);
                if (!registration) return;
                registration.state = {
                    position: { ...nextState.position },
                    size: { ...nextState.size },
                };
                this.clampWindowState(registration);
                this.applyWindowLayout(registration.definition, registration.state);
                this.save(definition.id);
                this.scheduleStateChange(registration);
            },
            getExtras: () => {
                const registration = this.windows.get(definition.id);
                return registration ? registration.extras : { ...(definition.defaultExtras ?? {}) };
            },
            setExtras: (extras) => {
                const registration = this.windows.get(definition.id);
                if (!registration) return;
                registration.extras = this.normalizeExtras(definition, extras);
                this.save(definition.id);
            },
            getWindowState: () => {
                const registration = this.windows.get(definition.id);
                const raw = registration?.extras.windowState;
                if (raw === 'open' || raw === 'minimized' || raw === 'closed') return raw;
                return this.defaultWindowState(definition.id);
            },
            setWindowState: (state) => {
                const registration = this.windows.get(definition.id);
                if (!registration) return;
                registration.extras = this.normalizeExtras(definition, {
                    ...registration.extras,
                    windowState: state,
                });
                this.applyViewState(registration);
                this.save(definition.id);
            },
            getActiveTab: () => {
                const registration = this.windows.get(definition.id);
                const raw = registration?.extras.activeTab;
                return typeof raw === 'string' && raw.trim().length > 0 ? raw : 'main';
            },
            setActiveTab: (tab) => {
                const registration = this.windows.get(definition.id);
                if (!registration) return;
                registration.extras = this.normalizeExtras(definition, {
                    ...registration.extras,
                    activeTab: tab,
                });
                this.save(definition.id);
            },
            destroy: () => this.destroy(definition.id),
        };
    }

    public get(id: string): WindowRegistration | undefined {
        const record = this.windows.get(id);
        if (!record) return undefined;
        return {
            definition: record.definition,
            state: record.state,
            extras: record.extras,
        };
    }

    public save(id: string): void {
        const registration = this.windows.get(id);
        if (!registration) return;
        if (registration.definition.persistState === false) return;
        saveWindowState(id, registration.state, registration.extras);
    }

    public destroy(id: string): void {
        const registration = this.windows.get(id);
        if (!registration) return;
        if (this.activePointerWindowId === id) {
            this.activePointerWindowId = null;
        }
        if (registration.stateChangeTimeoutId !== null) {
            window.clearTimeout(registration.stateChangeTimeoutId);
            registration.stateChangeTimeoutId = null;
        }
        this.detachWindowListeners(registration);
        if (registration.definition.persistState !== false) {
            saveWindowState(id, registration.state, registration.extras);
        }
        this.windows.delete(id);
        this.detachGlobalListenersIfUnused();
    }

    public clear(): void {
        for (const id of Array.from(this.windows.keys())) {
            this.destroy(id);
        }
    }

    public rehydrateAll(): void {
        for (const [id, record] of this.windows.entries()) {
            if (record.definition.persistState === false) {
                record.state = {
                    position: { ...record.definition.defaultPosition },
                    size: { ...record.definition.defaultSize },
                };
                record.extras = this.normalizeExtras(record.definition, record.definition.defaultExtras ?? {});
                this.clampWindowState(record);
                this.applyWindowLayout(record.definition, record.state);
                this.applyViewState(record, { suppressCallbacks: true });
                continue;
            }
            record.state = loadWindowState(
                id,
                {
                    position: record.definition.defaultPosition,
                    size: record.definition.defaultSize,
                },
                record.definition.minWidth,
                record.definition.minHeight
            );
            record.extras = this.normalizeExtras(
                record.definition,
                loadWindowExtras(id, record.definition.defaultExtras ?? {})
            );
            this.clampWindowState(record);
            this.applyWindowLayout(record.definition, record.state);
            this.applyViewState(record, { suppressCallbacks: true });
        }
    }

    public consumeEscape(): boolean {
        const topRecord = Array.from(this.windows.values())
            .filter((record) =>
                record.definition.consumeEscape !== false
                && !record.definition.container.hidden
                && record.definition.container.style.display !== 'none'
            )
            .sort((a, b) => b.runtime.zIndex - a.runtime.zIndex)[0];
        if (!topRecord) {
            return false;
        }

        if (topRecord.definition.closeButton) {
            topRecord.definition.closeButton.click();
        } else {
            topRecord.extras = this.normalizeExtras(topRecord.definition, {
                ...topRecord.extras,
                windowState: WINDOW_STATE_CLOSED,
            });
            this.applyViewState(topRecord);
            this.save(topRecord.definition.id);
        }

        return true;
    }

    private createRuntime(definition: WindowDefinition): WindowRuntime {
        const runtime: WindowRuntime = {
            isDragging: false,
            isResizing: false,
            dragStartX: 0,
            dragStartY: 0,
            startX: 0,
            startY: 0,
            startWidth: 0,
            startHeight: 0,
            zIndex: ++this.topZIndex,
            onHeaderMouseDown: (e: MouseEvent) => {
                const target = e.target as HTMLElement;
                if (definition.closeButton && (target === definition.closeButton || target.closest('button'))) return;
                if (runtime.isResizing) return;
                runtime.isDragging = true;
                runtime.dragStartX = e.clientX;
                runtime.dragStartY = e.clientY;
                runtime.startX = this.windows.get(definition.id)?.state.position.x ?? definition.defaultPosition.x;
                runtime.startY = this.windows.get(definition.id)?.state.position.y ?? definition.defaultPosition.y;
                this.activePointerWindowId = definition.id;
                this.bringToFront(definition.id);
            },
            onResizeMouseDown: (e: MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                runtime.isResizing = true;
                runtime.dragStartX = e.clientX;
                runtime.dragStartY = e.clientY;
                const record = this.windows.get(definition.id);
                runtime.startX = record?.state.position.x ?? definition.defaultPosition.x;
                runtime.startY = record?.state.position.y ?? definition.defaultPosition.y;
                runtime.startWidth = record?.state.size.width ?? definition.defaultSize.width;
                runtime.startHeight = record?.state.size.height ?? definition.defaultSize.height;
                this.activePointerWindowId = definition.id;
                this.bringToFront(definition.id);
            },
            onContainerMouseDown: () => {
                this.bringToFront(definition.id);
            },
        };

        definition.header.addEventListener('mousedown', runtime.onHeaderMouseDown);
        if (definition.resizeHandle && runtime.onResizeMouseDown) {
            definition.resizeHandle.addEventListener('mousedown', runtime.onResizeMouseDown);
        }
        definition.container.addEventListener('mousedown', runtime.onContainerMouseDown);
        return runtime;
    }

    private detachWindowListeners(record: WindowRecord): void {
        record.definition.header.removeEventListener('mousedown', record.runtime.onHeaderMouseDown);
        if (record.definition.resizeHandle && record.runtime.onResizeMouseDown) {
            record.definition.resizeHandle.removeEventListener('mousedown', record.runtime.onResizeMouseDown);
        }
        record.definition.container.removeEventListener('mousedown', record.runtime.onContainerMouseDown);
    }

    private attachGlobalListeners(): void {
        if (this.listenersAttached) return;
        window.addEventListener('mousemove', this.onMouseMove);
        window.addEventListener('mouseup', this.onMouseUp);
        window.addEventListener('resize', this.onWindowResize);
        this.listenersAttached = true;
    }

    private detachGlobalListenersIfUnused(): void {
        if (!this.listenersAttached || this.windows.size > 0) return;
        window.removeEventListener('mousemove', this.onMouseMove);
        window.removeEventListener('mouseup', this.onMouseUp);
        window.removeEventListener('resize', this.onWindowResize);
        this.listenersAttached = false;
    }

    private bringToFront(id: string): void {
        const record = this.windows.get(id);
        if (!record) return;
        record.runtime.zIndex = ++this.topZIndex;
        record.definition.container.style.zIndex = String(record.runtime.zIndex);
    }

    private handleGlobalMouseMove(e: MouseEvent): void {
        if (!this.activePointerWindowId) return;
        const record = this.windows.get(this.activePointerWindowId);
        if (!record) return;
        if (!record.runtime.isDragging && !record.runtime.isResizing) return;

        if (record.runtime.isDragging) {
            const dx = e.clientX - record.runtime.dragStartX;
            const dy = e.clientY - record.runtime.dragStartY;
            record.state.position = {
                x: record.runtime.startX + dx,
                y: record.runtime.startY + dy,
            };
            this.clampWindowState(record);
            this.applyWindowLayout(record.definition, record.state);
        }

        if (record.runtime.isResizing) {
            const dx = e.clientX - record.runtime.dragStartX;
            const dy = e.clientY - record.runtime.dragStartY;
            const maxWidth = Math.max(record.definition.minWidth, window.innerWidth - record.runtime.startX);
            const width = Math.min(Math.max(record.definition.minWidth, record.runtime.startWidth + dx), maxWidth);
            let height = record.runtime.startHeight - dy;
            let y = record.runtime.startY + dy;

            if (height < record.definition.minHeight) {
                height = record.definition.minHeight;
                y = record.runtime.startY + record.runtime.startHeight - record.definition.minHeight;
            }

            record.state.size = {
                width,
                height,
            };
            record.state.position = {
                x: record.state.position.x,
                y,
            };
            this.clampWindowState(record);
            this.applyWindowLayout(record.definition, record.state);
        }
    }

    private handleGlobalMouseUp(): void {
        if (!this.activePointerWindowId) return;
        const record = this.windows.get(this.activePointerWindowId);
        this.activePointerWindowId = null;
        if (!record) return;
        const moved = record.runtime.isDragging;
        const resized = record.runtime.isResizing;
        if (!moved && !resized) return;

        record.runtime.isDragging = false;
        record.runtime.isResizing = false;
        this.clampWindowState(record);
        this.applyWindowLayout(record.definition, record.state);
        saveWindowState(record.definition.id, record.state, record.extras);
        if (moved) record.definition.onMoveEnd?.(record.state);
        if (resized) record.definition.onResizeEnd?.(record.state);
        this.scheduleStateChange(record);
    }

    private handleWindowResize(): void {
        for (const record of this.windows.values()) {
            this.clampWindowState(record);
            this.applyWindowLayout(record.definition, record.state);
        }
    }

    private clampWindowState(record: WindowRecord): void {
        const width = Math.min(Math.max(record.definition.minWidth, record.state.size.width), window.innerWidth);
        const height = Math.min(Math.max(record.definition.minHeight, record.state.size.height), window.innerHeight);
        const maxX = Math.max(0, window.innerWidth - width);
        const maxY = Math.max(0, window.innerHeight - height);
        record.state.size = { width, height };
        record.state.position = {
            x: Math.min(Math.max(0, record.state.position.x), maxX),
            y: Math.min(Math.max(0, record.state.position.y), maxY),
        };
    }

    private applyWindowLayout(definition: WindowDefinition, state: WindowRegistration['state']): void {
        definition.container.style.left = `${state.position.x}px`;
        definition.container.style.top = `${state.position.y}px`;
        definition.container.style.width = `${state.size.width}px`;
        definition.container.style.height = `${state.size.height}px`;
    }

    private scheduleStateChange(record: WindowRecord): void {
        if (!record.definition.onStateChange) return;
        if (record.stateChangeTimeoutId !== null) {
            window.clearTimeout(record.stateChangeTimeoutId);
        }
        const debounceMs = record.definition.stateChangeDebounceMs ?? 500;
        record.stateChangeTimeoutId = window.setTimeout(() => {
            record.stateChangeTimeoutId = null;
            record.definition.onStateChange?.(record.state);
        }, debounceMs);
    }

    private defaultWindowState(id: string): WindowViewState {
        if (DEFAULT_OPEN_WINDOW_PREFIXES.some((prefix) => id.startsWith(prefix))) return WINDOW_STATE_OPEN;
        return WINDOW_STATE_CLOSED;
    }

    private normalizeExtras(definition: WindowDefinition, extras: WindowRegistration['extras']): WindowRegistration['extras'] {
        const next = { ...extras };
        const windowState = next.windowState;
        if (windowState !== 'open' && windowState !== 'minimized' && windowState !== 'closed') {
            next.windowState = this.defaultWindowState(definition.id);
        }
        const activeTab = next.activeTab;
        const defaultTab = typeof definition.defaultTab === 'string' && definition.defaultTab.trim().length > 0
            ? definition.defaultTab
            : 'main';
        const allowedTabs = definition.allowedTabs;
        if (typeof activeTab !== 'string' || activeTab.trim().length === 0) {
            next.activeTab = defaultTab;
        } else if (Array.isArray(allowedTabs) && allowedTabs.length > 0 && !allowedTabs.includes(activeTab)) {
            next.activeTab = defaultTab;
        }
        return next;
    }

    private applyViewState(record: WindowRecord, options?: { suppressCallbacks?: boolean }): void {
        const suppressCallbacks = options?.suppressCallbacks ?? false;
        const state = record.extras.windowState as WindowViewState;
        if (state === 'closed') {
            record.definition.container.hidden = true;
            record.definition.container.style.display = 'none';
            if (!suppressCallbacks) {
                record.definition.onClose?.();
            }
            return;
        }

        record.definition.container.hidden = false;
        if (record.definition.container.style.display === 'none') {
            record.definition.container.style.display = '';
        }
        if (!suppressCallbacks) {
            record.definition.onOpen?.();
        }
        this.bringToFront(record.definition.id);
    }
}

export const windowRegistry = new WindowRegistry();
